import React, { useCallback, useContext, useEffect, useState } from "react";
import { createSharedContext } from "../lib/utils";
import { setProjectHeaders } from "../services/api/base";

export const PROJECT_STORAGE_KEY = "9s_current_rag_project_id";

export interface CurrentProject {
  id: string;
  name: string;
}

interface ProjectContextValue {
  currentProject: CurrentProject | null;
  projectId: string | null;
  setCurrentProject: (project: CurrentProject | null) => void;
  isLoading: boolean;
}

const ProjectContext = createSharedContext<ProjectContextValue | null>("__9s_project_react_context__", null);

export function ProjectProvider({ children }: { children: React.ReactNode }) {
  const [currentProject, setCurrentProjectState] = useState<CurrentProject | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const setCurrentProject = useCallback((project: CurrentProject | null) => {
    setCurrentProjectState(project);
    setProjectHeaders(project ? { "X-Project-ID": project.id } : {});
    if (typeof window !== "undefined") {
      try {
        if (project) {
          window.localStorage.setItem(PROJECT_STORAGE_KEY, project.id);
        } else {
          window.localStorage.removeItem(PROJECT_STORAGE_KEY);
        }
      } catch {
        /* ignore */
      }
    }
  }, []);

  useEffect(() => {
    setIsLoading(false);
  }, []);

  const value: ProjectContextValue = {
    currentProject,
    projectId: currentProject?.id ?? null,
    setCurrentProject,
    isLoading,
  };

  return <ProjectContext.Provider value={value}>{children}</ProjectContext.Provider>;
}

// Hook exported with provider; same-file export is intentional.
// eslint-disable-next-line react-refresh/only-export-components
export function useProjectContext(): ProjectContextValue {
  const ctx = useContext(ProjectContext);
  if (!ctx) {
    throw new Error("useProjectContext must be used within ProjectProvider");
  }
  return ctx;
}
